import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useSearchParams,
  useNavigate,
} from "react-router-dom";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import type { ReactNode } from "react";
import { authService } from "./services/authService";
import { logger } from "./lib/logger";
import SessionList from "./pages/SessionList";
import ContextsPage from "./pages/ContextsPage";
import ContextSessionsPage from "./pages/ContextSessionsPage";
import SessionsManager from "./pages/SessionsManager";
import SessionSkills from "./pages/SessionSkills";
import TestRuns from "./pages/TestRuns";
import RecordingReplay from "./pages/RecordingReplay";
import AdminDashboard from "./pages/AdminDashboard";
import NavBar, { NAV_HEIGHT } from "./components/NavBar";

// ─── Auth Context ─────────────────────────────────────────────────────

interface AuthContextValue {
  isAuthenticated: boolean;
  login: () => void;
  logout: () => void;
  getToken: () => string | null;
}

const AuthContext = createContext<AuthContextValue>({
  isAuthenticated: false,
  login: () => {},
  logout: () => {},
  getToken: () => null,
});

/** Hook to access auth state and actions. */
export function useAuth(): AuthContextValue {
  return useContext(AuthContext);
}

// ─── Auth Provider ────────────────────────────────────────────────────

function AuthProvider({ children }: { children: ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(
    authService.isAuthenticated(),
  );

  useEffect(() => {
    // Wire logger to auth service for authenticated log shipping
    logger.setTokenGetter(() => authService.getToken());

    // Re-sync on auth state changes (login, logout, refresh)
    const unsubscribe = authService.subscribe(() => {
      setIsAuthenticated(authService.isAuthenticated());
    });
    return unsubscribe;
  }, []);

  const login = useCallback(() => {
    authService.login();
  }, []);

  const logout = useCallback(() => {
    authService.logout();
  }, []);

  const getToken = useCallback(() => {
    return authService.getToken();
  }, []);

  return (
    <AuthContext.Provider value={{ isAuthenticated, login, logout, getToken }}>
      {children}
    </AuthContext.Provider>
  );
}

// ─── Auth bypass flag ─────────────────────────────────────────────────

const AUTH_DISABLED = import.meta.env.VITE_AUTH_DISABLED === "true";
const SKILLS_RETURN_KEY = "skills_return_after_login";
const SKILLS_PATH = /^\/sessions\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\/skills$/i;

// ─── Auth Guard ───────────────────────────────────────────────────────

function AuthGuard({ children }: { children: ReactNode }) {
  const { isAuthenticated, login, logout } = useAuth();
  const logoutPending = authService.isLogoutPending();

  useEffect(() => {
    if (!AUTH_DISABLED && !isAuthenticated && !logoutPending) {
      // A separate workspace gets its own normal SSO round trip: tokens are
      // memory-only. Preserve just this local, non-video destination.
      if (SKILLS_PATH.test(window.location.pathname)) sessionStorage.setItem(SKILLS_RETURN_KEY, window.location.pathname);
      else sessionStorage.removeItem(SKILLS_RETURN_KEY);
      login();
    }
  }, [isAuthenticated, login, logoutPending]);

  if (!AUTH_DISABLED && !isAuthenticated && logoutPending) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          gap: 16,
          color: "#e5e7eb",
          fontFamily: "system-ui, sans-serif",
        }}
      >
        <div>You are logged out.</div>
        <button
          onClick={login}
          style={{
            background: "#2563eb",
            color: "#fff",
            border: "none",
            borderRadius: 6,
            padding: "8px 16px",
            cursor: "pointer",
          }}
        >
          Sign in again
        </button>
      </div>
    );
  }

  if (!AUTH_DISABLED && !isAuthenticated) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          color: "#9ca3af",
          fontFamily: "system-ui, sans-serif",
        }}
      >
        Redirecting to login...
      </div>
    );
  }

  // D15c3 : wrapper paddingTop = NAV_HEIGHT pour compenser la NavBar position:fixed.
  // Une seule instance NavBar (cycle 2 avait 2 branches distinctes).
  return (
    <>
      <NavBar onLogout={logout} />
      <AuthenticatedContent>{children}</AuthenticatedContent>
    </>
  );
}

/**
 * The document root stays fixed for streaming views. Protected list pages use
 * this single, viewport-sized scroll owner so long session/context collections
 * remain reachable without introducing nested page scrollbars.
 */
export function AuthenticatedContent({ children }: { children: ReactNode }) {
  return (
    <div
      data-testid="authenticated-content"
      style={{
        boxSizing: "border-box",
        height: "100%",
        minHeight: 0,
        paddingTop: NAV_HEIGHT,
        overflowX: "hidden",
        overflowY: "auto",
        scrollbarGutter: "stable",
      }}
    >
      {children}
    </div>
  );
}

// ─── Auth Callback Handler ────────────────────────────────────────────

function AuthCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const code = searchParams.get("code");
    const state = searchParams.get("state");

    if (!code || !state) {
      setError("Missing code or state parameter");
      return;
    }

    authService
      .handleCallback(code, state)
      .then(() => {
        const destination = sessionStorage.getItem(SKILLS_RETURN_KEY) ?? "";
        sessionStorage.removeItem(SKILLS_RETURN_KEY);
        navigate(SKILLS_PATH.test(destination) ? destination : "/contexts", { replace: true });
      })
      .catch((e: unknown) => {
        setError(e instanceof Error ? e.message : "Authentication failed");
      });
  }, [searchParams, navigate]);

  if (error !== null) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          gap: 16,
          color: "#e5e7eb",
          fontFamily: "system-ui, sans-serif",
        }}
      >
        <div
          role="alert"
          style={{
            background: "#7f1d1d",
            color: "#fca5a5",
            padding: "12px 16px",
            borderRadius: 8,
          }}
        >
          {error}
        </div>
        <button
          onClick={() => navigate("/", { replace: true })}
          style={{
            background: "#2563eb",
            color: "#fff",
            border: "none",
            borderRadius: 6,
            padding: "8px 16px",
            cursor: "pointer",
          }}
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
        color: "#9ca3af",
        fontFamily: "system-ui, sans-serif",
      }}
    >
      Completing authentication...
    </div>
  );
}

// ─── App ──────────────────────────────────────────────────────────────

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <div
          style={{
            width: "100%",
            height: "100%",
            background: "#0a0a0a",
            color: "#e5e7eb",
          }}
        >
          <Routes>
            {/* Contexts are the authenticated entry point. */}
            <Route path="/" element={<Navigate to="/contexts" replace />} />

            {/* Auth callback (no guard — handles unauthenticated state) */}
            <Route path="/callback" element={<AuthCallback />} />

            {/* Protected routes */}
            <Route
              path="/contexts"
              element={
                <AuthGuard>
                  <ContextsPage />
                </AuthGuard>
              }
            />
            <Route
              path="/contexts/:contextId/sessions"
              element={
                <AuthGuard>
                  <ContextSessionsPage />
                </AuthGuard>
              }
            />
            <Route
              path="/sessions"
              element={
                <AuthGuard>
                  <SessionList />
                </AuthGuard>
              }
            />
            <Route
              path="/sessions/new"
              element={
                <AuthGuard>
                  <Navigate to="/contexts" replace />
                </AuthGuard>
              }
            />
            <Route
              path="/sessions/:id/skills"
              element={<AuthGuard><SessionSkills /></AuthGuard>}
            />
            <Route
              path="/sessions/:id"
              element={
                <AuthGuard>
                  <SessionsManager />
                </AuthGuard>
              }
            />
            <Route
              path="/test-runs"
              element={
                <AuthGuard>
                  <TestRuns />
                </AuthGuard>
              }
            />
            <Route
              path="/admin"
              element={
                <AuthGuard>
                  {authService.isAdmin() ? (
                    <AdminDashboard />
                  ) : (
                    <Navigate to="/contexts" replace />
                  )}
                </AuthGuard>
              }
            />

            <Route
              path="/sessions/:id/replay"
              element={
                <AuthGuard>
                  <RecordingReplay />
                </AuthGuard>
              }
            />

            {/* Catch-all redirect */}
            <Route path="*" element={<Navigate to="/contexts" replace />} />
          </Routes>
        </div>
      </AuthProvider>
    </BrowserRouter>
  );
}
